/**
 * Date protection, as the Activity reads it off a cap (CU-869fe8jrk).
 *
 * A protection is a price limit whose dates mean "any rental that
 * crosses them". The engine keeps `reason = 'price_limit'` on its cap —
 * same stage of the rail, same override — and says what it is in
 * `source.kind`. Everything on screen that distinguishes the two reads
 * the key below, never the reason alone, so the row, the rail, the
 * Limits block and the filter can never disagree (UX gate, 8a).
 */

export const DATE_PROTECTION = 'date_protection'

/** The key a cap is labelled, filtered and counted by. */
export function capKey(cap) {
	if (cap?.reason === 'price_limit' && cap?.source?.kind === DATE_PROTECTION) return DATE_PROTECTION
	return cap?.reason
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function parseDay(v) {
	if (!v) return null
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v))
	if (!m) return null
	return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) }
}

function dayLabel(x, withYear) {
	return `${x.d} ${MONTHS[x.m - 1]}${withYear ? ` ${x.y}` : ''}`
}

/** `26–31 Dec` · `31 Dec 2026–1 Jan 2027` · `from 26 Dec` · `until 31 Dec`. */
export function dateRangeLabel(from, to) {
	const a = parseDay(from), b = parseDay(to)
	if (a && b) {
		if (a.y !== b.y) return `${dayLabel(a, true)}–${dayLabel(b, true)}`
		if (a.m === b.m) return `${a.d}–${dayLabel(b, false)}`
		return `${dayLabel(a, false)}–${dayLabel(b, false)}`
	}
	if (a) return `from ${dayLabel(a, false)}`
	if (b) return `until ${dayLabel(b, false)}`
	return 'any dates'
}

/**
 * "this 5-day rental from 22 Dec crosses 26–31 Dec" — composed from the
 * cell and the cap's own dates, so an event keeps telling the truth
 * after the protection is edited or deleted.
 */
export function crossingSentence({ pickup, days, from, to }) {
	const p = parseDay(pickup)
	const range = dateRangeLabel(from, to)
	const length = days ? `${days}-day rental` : 'rental'
	if (!p) return `this ${length} crosses ${range}`
	return `this ${length} from ${dayLabel(p, false)} crosses ${range}`
}

/** What the cap has to say for itself when it is a protection. */
export function protectionNotes(cap, row) {
	const src = cap?.source || {}
	const value = src.limit_value ?? cap?.to
	const amount = value == null ? '' : `${Number(value).toFixed(2)} €/day`
	return [
		[`minimum ${amount}`, src.label].filter(Boolean).join(' · '),
		`${crossingSentence({ pickup: row?.scope?.pickup_date, days: row?.scope?.duration_days,
		                      from: src.date_from, to: src.date_to })} — the whole rental takes the floor`,
	]
}
